/**
 * The caret half of the composer's split Send button: "Schedule send" with
 * three presets (Later today 5 PM / Tomorrow 9 AM / Monday 9 AM, local time,
 * past ones hidden) and "Custom date & time…", which opens a small dialog with
 * the app's native date and time inputs. Validation mirrors the backend:
 * at least 1 minute and at most 365 days ahead.
 */

import React, { useState } from 'react';
import { CalendarClock, ChevronDown, Clock } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  ScheduleError,
  SchedulePresetId,
  formatScheduleTime,
  fromDateTimeInputs,
  schedulePresets,
  toDateInputValue,
  toTimeInputValue,
  validateScheduledAt,
} from './scheduleSend';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { toZonedInputs, zonedInputsToMs } from '@/utils/zonedTime';
import { TimeZoneSelect } from '@/components/ui/TimeZoneSelect';

export function useScheduleErrorText() {
  const { t } = useAppTranslation();
  return (err: ScheduleError | null): string | null => {
    if (err === 'invalid') return t('messaging.schedule.errorInvalid', 'Pick a date and a time.');
    if (err === 'tooSoon') return t('messaging.schedule.errorTooSoon', 'Pick a time at least 1 minute from now.');
    if (err === 'tooFar') return t('messaging.schedule.errorTooFar', 'Messages can be scheduled up to 365 days ahead.');
    return null;
  };
}

/** Native date + time inputs with the backend's window, shared by the panel's Edit form. */
export function ScheduleDateTimeFields({
  date,
  time,
  onDateChange,
  onTimeChange,
  error,
  idPrefix,
}: {
  date: string;
  time: string;
  onDateChange: (v: string) => void;
  onTimeChange: (v: string) => void;
  error: string | null;
  idPrefix: string;
}) {
  const { t } = useAppTranslation();
  const now = new Date();
  const max = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <label className="flex min-w-[9rem] flex-1 flex-col gap-1 text-sm" htmlFor={`${idPrefix}-date`}>
          <span className="text-muted-foreground">{t('messaging.schedule.date', 'Date')}</span>
          <Input
            id={`${idPrefix}-date`}
            type="date"
            value={date}
            min={toDateInputValue(now)}
            max={toDateInputValue(max)}
            onChange={(e) => onDateChange(e.target.value)}
            data-testid="schedule-date"
          />
        </label>
        <label className="flex min-w-[7rem] flex-1 flex-col gap-1 text-sm" htmlFor={`${idPrefix}-time`}>
          <span className="text-muted-foreground">{t('messaging.schedule.time', 'Time')}</span>
          <Input
            id={`${idPrefix}-time`}
            type="time"
            value={time}
            onChange={(e) => onTimeChange(e.target.value)}
            data-testid="schedule-time"
          />
        </label>
      </div>
      {error && (
        <p className="text-sm text-destructive" role="alert" data-testid="schedule-error">
          {error}
        </p>
      )}
    </div>
  );
}

interface ScheduleSendMenuProps {
  disabled?: boolean;
  /** Match the Send button when the draft is urgent. */
  urgent?: boolean;
  /** Schedule the current draft for this moment (and, from the custom dialog, the zone it was picked in). */
  onSchedule: (when: Date, timezone?: string) => void;
}

export function ScheduleSendMenu({ disabled, urgent, onSchedule }: ScheduleSendMenuProps) {
  const { t, language } = useAppTranslation();
  const errorText = useScheduleErrorText();
  const [menuOpen, setMenuOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  // TRUS-697: the custom date + time are wall-clock time in this zone (default the user's).
  const userTimezone = useUserTimezone();
  const [zone, setZone] = useState(userTimezone);
  const [submitted, setSubmitted] = useState(false);
  // Recomputed whenever the menu opens, so "Later today" disappears after 5 PM.
  const presets = menuOpen ? schedulePresets(new Date()) : [];

  const presetLabel = (id: SchedulePresetId) =>
    id === 'laterToday'
      ? t('messaging.schedule.presetLaterToday', 'Later today')
      : id === 'tomorrow'
        ? t('messaging.schedule.presetTomorrow', 'Tomorrow')
        : t('messaging.schedule.presetMonday', 'Monday');

  const openCustom = () => {
    const start = new Date(Date.now() + 60 * 60 * 1000);
    start.setMinutes(0, 0, 0);
    const local = toZonedInputs(start, zone);
    setDate(local.date || toDateInputValue(start));
    setTime(local.time || toTimeInputValue(start));
    setSubmitted(false);
    setCustomOpen(true);
  };

  const zonedMs = zonedInputsToMs(date, time, zone);
  const when = zonedMs != null ? new Date(zonedMs) : fromDateTimeInputs(date, time);
  const err = validateScheduledAt(when, new Date());
  const submitCustom = () => {
    setSubmitted(true);
    if (err || !when) return;
    setCustomOpen(false);
    onSchedule(when, zone);
  };

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant={urgent ? 'danger' : 'default'}
            disabled={disabled}
            className="rounded-l-none border-l border-primary-foreground/30 px-2"
            aria-label={t('messaging.schedule.menuLabel', 'Schedule send')}
            title={t('messaging.schedule.menuLabel', 'Schedule send')}
            data-testid="schedule-send-trigger"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        {/* Keep clear of the support launcher, which floats just above the
            composer's right end (where this menu opens). */}
        <DropdownMenuContent
          align="end"
          side="top"
          collisionPadding={{ right: 96, top: 8, left: 8, bottom: 8 }}
          sticky="always"
          className="w-64"
          data-testid="schedule-send-menu"
        >
          <DropdownMenuLabel className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4" />
            {t('messaging.schedule.menuTitle', 'Schedule send…')}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {presets.map((p) => (
            <DropdownMenuItem
              key={p.id}
              onSelect={() => onSchedule(p.at)}
              data-testid={`schedule-preset-${p.id}`}
              className="flex items-center justify-between gap-3"
            >
              <span>{presetLabel(p.id)}</span>
              <span className="text-xs text-muted-foreground">{formatScheduleTime(p.at, language)}</span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={openCustom} data-testid="schedule-custom">
            <Clock className="mr-2 h-4 w-4" />
            {t('messaging.schedule.custom', 'Custom date & time…')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('messaging.schedule.customTitle', 'Schedule message')}</DialogTitle>
            <DialogDescription>
              {t('messaging.schedule.customHintZone', 'The date and time are in the time zone you pick below.')}
            </DialogDescription>
          </DialogHeader>
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              submitCustom();
            }}
          >
            <ScheduleDateTimeFields
              idPrefix="schedule-custom"
              date={date}
              time={time}
              onDateChange={setDate}
              onTimeChange={setTime}
              error={submitted ? errorText(err) : null}
            />
            <div className="mt-3 flex flex-col gap-1 text-sm">
              <label htmlFor="schedule-custom-zone" className="text-muted-foreground">
                {t('time.zone.label', 'Time zone')}
              </label>
              <TimeZoneSelect
                id="schedule-custom-zone"
                value={zone}
                onChange={setZone}
                userTimeZone={userTimezone}
                at={zonedMs ?? undefined}
                data-testid="schedule-timezone"
              />
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setCustomOpen(false)}>
                {t('messaging.channel.cancel', 'Cancel')}
              </Button>
              <Button type="submit" data-testid="schedule-custom-submit">
                {t('messaging.schedule.submit', 'Schedule')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ScheduleSendMenu;
